<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <h5 id="draft-invoice-title" class="mb-0"><i class="fa fa-fw fa-ticket mr-2" />Draft Invoice</h5>
            <hr />
            <b-row>
                <b-col id="invoice-details" md="6">
                    Invoice untuk <strong>{{ data.customer_name }}</strong><br />
                    Segmen harga <strong>{{ parsePriceSegment(data.price_segment) }}</strong> <br />
                    Dari gudang <strong>{{ data.warehouse }} - {{ data.office }}</strong><br />
                    Divisi <strong>{{ data.division }}</strong><br />
                    Tipe Invoice <strong>{{ invoiceTypeLabel }}</strong> <br />
                    {{ data.total_item }} barang, total Rp. {{ $helper.formatThousand(data.total_price) }} <br />
                    Total berat bersih: <strong>{{ (data.total_net_weight == 0) ? "-" : data.total_net_weight }} gram</strong> <br />
                </b-col>
                <b-col id="invoice-actions" md="6" class="text-right">
                    <h5 v-if="data.over_limit_request_status != null" class="font-weight-bold">
                        Request Overlimit: <b-badge :variant="statusOverlimitBadge">{{ statusOverlimit }}</b-badge>
                    </h5>
                    <b-button class="mr-2" @click="openModalDiscountPurity" v-if="isOriginalPrice">
                        <i class="fa fa-calculator mr-2" />Hitung Diskon Kadar
                    </b-button>
                    <b-button id="cancel-invoice-button" variant="danger" @click="promptCancelInvoice">
                        <i class="fa fa-times mr-2" />Batalkan
                    </b-button>
                    <b-button id="finalize-invoice-button" variant="success" class="ml-2" @click="finalizeInvoice">
                        <i class="fa fa-check mr-2" />Finalisasi
                    </b-button><br>
                    <b-button id="edit-customer-button" variant="primary" class="mt-3" @click="promptEditInvoice">
                        <i class="fa fa-pencil mr-2" />Ubah Customer
                    </b-button>
                </b-col>
            </b-row>
            <b-row class="mt-3" id="search-item-row">
                <b-col md="6" id="search-item-col">
                    <b-input-group id="search-item-input-group">
                        <b-input v-model="input.noFaktur" maxlength="100" style="max-width: 250px" placeholder="Input No. Faktur..." @keydown.enter="findItem" id="search-item-input" />
                        <b-input-group-append id="search-item-input-group-append">
                            <b-button variant="dark" @click="findItem" id="search-item-button">Cari Barang</b-button>
                        </b-input-group-append>
                        <b-button variant="success" class="ml-2" @click="openModalImport">Import Excel</b-button>
                    </b-input-group>
                </b-col>
            </b-row>
            <b-table id="invoice-items-table" bordered hover show-empty no-local-sorting
                :items="data.items"
                :fields="filteredFields"
                responsive class="my-3"
                @sort-changed="onSortChanged"
            >
                <template v-slot:empty>
                    Invoice masih kosong.
                </template>
                <template v-slot:cell(barang)="row">
                    {{ row.item.type }} <span v-if="row.item.year != null">({{ row.item.year }})</span>
                    <div class="small text-muted">
                        {{ row.item.kind }} - {{ row.item.brand }}<br />
                        Berat Bersih: {{ (row.item.net_weight == 0) ? "-" : row.item.net_weight  }} gram<br />
                        Ctt: {{ row.item.note || "-" }}
                    </div>
                </template>
                <template v-slot:cell(noFakturImei)="row">
                    {{ row.item.no_faktur_pgi }}
                    <div class="small text-muted">
                        Grade <strong>{{ row.item.grade }} - {{ row.item.spec }} - {{ row.item.is_batangan ? 'BATANG' : 'LENGKAP' }}</strong><br />
                        Kloter {{ $helper.parseDate(row.item.created) }}
                    </div>
                </template>
                <template v-slot:cell(basePrice)="row">
                    <div v-if="isMeltedSale">
                        -
                    </div>
                    <div v-else>
                        Rp. {{ $helper.formatThousand(row.item.base_price) }}
                    </div>
                </template>
                <template v-slot:cell(adjustment)="row">
                    <div v-if="isMeltedSale">
                        -
                    </div>
                    <div v-else>
                        <p class="mb-0" v-if="row.item.adjustment_value">
                            Rp. {{ $helper.formatThousand(row.item.adjustment_value) }}
                        </p>
                        <p class="mb-0" v-if="isAuction">
                            Total Tambahan: Rp. {{ $helper.formatThousand(row.item.price_adjustment || 0) }}
                        </p>
                        <p class="mb-0" v-if="row.item.discount_purity">
                            Total Adjustment: Rp. {{ $helper.formatThousand(getAdjustmentValue(row.item)) }}
                        </p>
                        <div class="small text-muted" v-if="row.item.discount_purity">
                            Adjustment kadar emas: {{ row.item.discount_purity }}<br />
                        </div>
                        <p class="mb-0" v-if="!row.item.adjustment_value && !isAuction && !row.item.discount_purity">
                            Rp. 0
                        </p>
                    </div>
                </template>
                <template v-slot:cell(subtotal)="row">
                    <div v-if="isMeltedSale">
                        -
                    </div>
                    <div v-else>
                        Rp. {{ $helper.formatThousand(row.item.total) }}
                    </div>
                </template>
                <template v-slot:cell(action)="row">
                    <b-button id="edit-item-button" size="sm" variant="warning" class="mr-2" v-b-tooltip.hover.top title="Edit" @click="promptEditItem(row.item)">
                        <i class="fa fa-fw fa-pencil" />
                    </b-button>
                    <b-button id="remove-item-button" size="sm" variant="danger" v-b-tooltip.hover.top title="Hapus" @click="promptRemoveItem(row.item)">
                        <i class="fa fa-fw fa-times" />
                    </b-button>
                </template>
            </b-table>
        </b-card>
        <b-modal id="m-photo" title="Foto Barang" hide-footer>
            No. Faktur <strong>{{ activeItem.no_faktur_pgi }}</strong><br />
            {{ activeItem.type }} <span v-if="activeItem.year != null">({{ activeItem.year }})</span> - {{ activeItem.brand }} - {{ activeItem.kind }}
            <br /><br />
            <img :src="$BASE_FILE_PATH + '/' + activeItem.photo" width="100%" />
        </b-modal>
        <b-modal id="m-item-to-add" size="xl" :title="invoiceDetailId == null ? 'Penambahan Barang' : 'Pengubahan Barang'" centered no-close-on-backdrop no-close-on-esc hide-footer @hide="onItemModalHide">
            <ModalItemGoldDrafting :noFaktur="noFakturPGI" :invoiceId="Number($route.params.id)" :invoiceDetailId="invoiceDetailId" @item-added="onItemAdded" :invoiceType="data.invoice_type" />
        </b-modal>
        <!-- modal finalize for gold -->
        <b-modal id="m-finalize-disc-gold" title="Finalisasi - Diskon Emas" centered no-close-on-esc hide-footer>
            <b-form-group label="No. Ref Invoice" description="Wajib diisi. No. Ref akan ditampilkan di invoice. Maks. 20 karakter.">
                <b-input v-model="finalize.noRef" maxlength="20" />
            </b-form-group>
            <b-form-group label="Tanggal Penjualan" description="Wajib diisi. Tanggal Penjualan yang akan dimunculkan di cetakan invoice sales.">
                <b-input v-model="finalize.soldAt" type="date" />
            </b-form-group>
            <b-form-group label="Catatan Invoice / Diskon (Opsional)">
                <b-input  v-model="finalize.note" maxlength="200" />
            </b-form-group>
            <div v-if="isMeltedSale">
                <b-form-group label="Harga Final" description="Wajib diisi. Harga final untuk jual lebur">
                    <b-input-group prepend="Rp.">
                        <b-form-input v-model="finalize.finalPrice" maxlength="50" placeholder="Masukan Harga Final"
                        @input="sanitizeCapital" onkeypress="return event.charCode >= 48 && event.charCode <= 57" />
                    </b-input-group>
                </b-form-group>
            </div>
            <div v-if="data.discount_purity">
                <strong>Diskon Kadar Emas: </strong>
                {{ data.discount_purity }}
            </div>
            <div>
                <b-form-group label="PPH (Opsional)">
                    <multiselect v-model="finalize.pph"
                        label="name"
                        track-by="id"
                        placeholder="Pilih PPH..."
                        deselect-label="Hapus"
                        select-label=""
                        :options="optPph"
                    />
                </b-form-group>
                <b-form-group label="Materai (Opsional)">
                    <multiselect v-model="finalize.materai"
                        label="name"
                        track-by="id"
                        placeholder="Pilih Materai..."
                        deselect-label="Hapus"
                        select-label=""
                        :options="optMaterai"
                    />
                </b-form-group>
            </div>
            <div class="text-right mt-4">
                <b-button id="continue-button" variant="primary" :disabled="isSubmitting" @click="moveToConfirmation">
                    Lanjutkan
                </b-button>
            </div>
        </b-modal>
        <b-modal id="m-finalize-confirm" title="Finalisasi Invoice" centered no-close-on-backdrop no-close-on-esc hide-footer :hide-header-close="isSubmitting">
            Konfirmasi akhir untuk pengukuhan invoice.
            <div class="mt-3">
            <div id="finalize-confirm-no-ref">
                <strong>No. Ref</strong><br />
                {{ finalData.no_ref }}
            </div>
            <div id="finalize-confirm-sold-at" class="mt-3">
                <strong>Tanggal Penjualan</strong><br />
                {{ $helper.parseDate(finalData.sold_at) }}
            </div>
            <div id="finalize-confirm-customer" class="mt-3">
                <strong>Customer</strong><br />
                {{ finalData.customer }}
            </div>
            <div id="finalize-confirm-note" class="mt-3">
                <strong>Catatan</strong><br />
                {{ finalize.note || "-" }}
            </div>
            <div id="finalize-confirm-total-item" class="mt-3">
                <strong>Total Belanja</strong><br />
                <span v-if="isBuyback">{{ finalData.total_item }} item(s), Rp. {{ $helper.formatThousand(finalData.subtotal) }}</span>
                <span v-else>{{ finalData.total_item }} item(s), Rp. {{ $helper.formatThousand(finalData.total_price) }}</span>
            </div>
            <div class="mt-3">
                <strong>PPH</strong><br />
                Rp. {{ $helper.formatThousand(finalData.pph || 0) }}
            </div>
            <div class="mt-3">
                <strong>Materai</strong><br />
                Rp. {{ $helper.formatThousand(finalData.stamp_duty || 0) }}
            </div>
            <template v-if="!isBuyback">
                <div id="finalize-confirm-total-adjustment" class="mt-3">
                    <strong>Total Adjustment</strong><br />
                    Rp. {{ $helper.formatThousand(finalData.total_adjustment || 0) }}
                </div>
                <div id="finalize-confirm-discount-percent" class="mt-3">
                    <strong>Diskon Kadar</strong><br />
                    {{ finalData.discount_purity }}
                </div>
            </template>
            <div id="finalize-confirm-total-price-after-discount" class="mt-3">
                <strong v-if="isBuyback">Total</strong>
                <strong v-else>Total Pembayaran Akhir (Setelah Dikurangi Diskon)</strong><br />
                <span v-if="isBuyback">Rp. {{ $helper.formatThousand(finalData.total) }}</span>
                <span v-else>Rp. {{ $helper.formatThousand(finalData.total_price_after_discount) }}</span>
            </div>
            <div id="finalize-confirm-deposit" class="mt-3">
                <strong>Sisa Deposit Customer</strong><br />
                Rp. {{ $helper.formatThousand(finalData.deposit) }}
            </div>
            <div id="finalize-confirm-final-price" class="mt-3">
                <strong>Harga Akhir (Setelah Dikurangi Deposit)</strong><br />
                Rp. {{ $helper.formatThousand(finalData.final_price) }}
            </div>
            <div id="finalize-confirm-action" class="mt-4 text-right">
                <b-button id="finalize-confirm-button" variant="primary" :disabled="isSubmitting" @click="execConfirmInvoice">
                {{ isSubmitting ? "Memproses..." : "Transaksikan Invoice" }}
                </b-button>
            </div>
            </div>
        </b-modal>
        <b-modal id="m-pick-price-segment" title="Set Segmen Harga" centered size="sm" hide-footer no-close-on-backdrop no-close-on-esc>
            <b-button id="segment-grosir-offline" block :variant="data.price_segment == 1 ? 'primary' : 'light'" :disabled="isSubmitting" @click="switchSegment(1)">Grosir Offline</b-button>
            <b-button id="segment-grosir-online" block :variant="data.price_segment == 2 ? 'primary' : 'light'" :disabled="isSubmitting" @click="switchSegment(2)">Grosir Online</b-button>
            <b-button id="segment-retail" block :variant="data.price_segment == 3 ? 'primary' : 'light'" :disabled="isSubmitting" @click="switchSegment(3)">Retail</b-button>
        </b-modal>
        <b-modal id="m-pin" :title="pinTitle" centered hide-footer>
            <b-form-group id="pin-input-group" label="Masukan PIN">
                <b-input type="password" v-model="pin" maxlength="10" @keydown.enter="checkPIN" autofocus />
            </b-form-group>
            <div class="mt-4 text-right">
                <b-button variant="primary" :disabled="isSubmitting" @click="checkPIN">
                    Otorisasi
                </b-button>
            </div>
        </b-modal>
        <b-modal id="m-edit" title="Ubah Customer" centered hide-footer>
            <b-form-group id="edit-customer-group" label="Invoice ditujukan untuk...">
                <div id="edit-customer-select">
                    <multiselect id="edit-customer-select" v-model="edit.customer"
                        label="name"
                        track-by="id"
                        :internal-search="true"
                        deselect-label=""
                        select-label=""
                        :options="edit.optCustomer"
                    />
                </div>
            </b-form-group>
            <div class="mt-4 text-right">
            <b-button id="save-edit-customer-button" variant="primary" :disabled="isSubmitting || edit.customer == null" @click="confirmEditInvoice">
                {{ isSubmitting ? "Menyimpan..." : "Simpan" }}
            </b-button>
            </div>
        </b-modal>
        <b-modal id="m-print" title="Cetak Invoice" centered hide-footer no-close-on-backdrop no-close-on-esc hide-header-close>
            <div id="print-invoice-message">Invoice berhasil dibuat. Tekan bagian "Sinar Mutiara Cell" untuk mencetak.</div>
            <iframe id="invoice-print-frame" :src="getInvoiceLink()" height="300px" width="100%" />
            <div id="print-invoice-instruction">Pastikan invoice sudah tercetak baik sebelum menekan tombol OK.</div>
            <div class="mt-4">
            <b-row>
                <b-col md="6">
                <b-button id="toggle-print-size-button" variant="dark" :disabled="invoiceSizeCooldown" @click="toggleInvoiceSize">
                    Print {{ invoiceSize == 'A5' ? 'A4' : 'A5' }}
                </b-button>
                </b-col>
                <b-col md="6" class="text-right">
                <b-button id="finish-invoice-button" variant="primary" @click="finishInvoice">
                    OK
                </b-button>
                </b-col>
            </b-row>
            </div>
        </b-modal>
        <b-modal
            id="m-overlimit-request"
            title="Request Overlimit"
            centered
            hide-footer
        >
            <p>Apakah anda yakin ingin meminta overlimit untuk customer ini?</p>
            <b-row>
                <b-col md="12" class="text-right">
                    <b-button
                        variant="outline-primary"
                        class="mr-2"
                        @click="$bvModal.hide('m-overlimit-request')"
                    >
                        Batal
                    </b-button>
                    <b-button
                        variant="primary"
                        @click="requestOverlimit"
                    >
                        {{ isSubmitting ? "Menyimpan..." : "Ya" }}
                    </b-button>
                </b-col>
            </b-row>
        </b-modal>
        <b-modal id="m-discount-purity" title="Diskon Kadar Emas" centered hide-footer>
            <b-form-group>
                <b-input v-model="finalize.discountPurity" max="1000" min="1" />
                <small class="text-muted">
                    * Masukkan angka (1 hingga 1000) untuk <strong>menaikkan harga</strong> berdasarkan kadar.
                </small> <br />
                <small class="text-muted">
                    * Masukkan angka (-1 hingga -1000) untuk <strong>menurunkan harga</strong> berdasarkan kadar.
                </small> <br />
                <small class="text-muted">
                    * Masukkan 0 untuk <strong>menghapus penyesuaian</strong> yang sudah diterapkan.
                </small>
            </b-form-group>
            <div class="mt-4 text-right">
                <b-button id="continue-button" variant="primary" :disabled="isSubmitting" @click="submitDiscountPurity">
                    <b-spinner v-if="isSubmitting" small />
                    <span v-else>Lanjutkan</span>
                </b-button>
            </div>
        </b-modal>
        <b-modal id="m-upload" title="Upload excel item" centered no-close-on-backdrop no-close-on-esc hide-footer>
            <b-form-group>
                <b-form-file v-model="input.fileUpload" :state="Boolean(input.fileUpload)" placeholder="Pilih file excel" drop-placeholder="Tarik dan lepas file disini" accept=".xlsx, .xls" />
                <small class="text-muted">* File harus menggunakan format sesuai jenis invoice:</small> <br />
                <small class="text-muted">• Untuk tipe invoice <strong>Lelang</strong>: wajib mencantumkan <em>Nomor Faktur</em> dan <em>Harga Jual</em></small> <br />
                <small class="text-muted">• Untuk tipe invoice <strong>Harga Asli</strong> dan <strong>Jual Lebur</strong>: cukup mencantumkan <em>Nomor Faktur</em></small> <br />
                <small class="text-muted">* Baris pertama akan diabaikan (anggap sebagai header)</small>
            </b-form-group>
            <b-button variant="primary" class="mt-2" @click="importExcel" :disabled="!input.fileUpload || isSubmitting">
                <i class="fa fa-upload mr-2" v-if="!isSubmitting" />
                {{ isSubmitting ? 'Mengupload...' : 'Unggah' }}
            </b-button>
        </b-modal>
    </div>
</template>

<script>
import ModalItemGoldDrafting from './ModalItemGoldDrafting.vue'

export default {
    components: {
        ModalItemGoldDrafting
    },
    data() {
        return {

            initStatus: 0,
            isSubmitting: false,
            isFetching: false,

            noFakturPGI: "",
            invoiceDetailId: null,
            pin: "",
            pinTitle: "",
            afterPin: "",
            invoiceLink: "",
            invoiceLinkA4: "",

            data: {
                items: []
            },

            input: {
                noFaktur: "",
                fileUpload: null,
            },

            finalize: {
                discount: "",
                discountAll: "",
                discountPercent: "",
                dp: "",
                fullPayment: true,
                note: "",
                noRef: "",
                soldAt: "",
                discountPurity: "",
                finalPrice: null,
                pph: null,
                materai: null,

                totalPriceAfterDiscount: 0
            },

            finalData: {},

            optPph: [],
            optMaterai: [],

            fields: [
                { key: "barang", label: "Barang", sortable: true },
                { key: "noFakturImei", label: "No. Faktur & IMEI / SN", sortable: true },
                { key: "basePrice", label: "Harga Dasar", sortable: true },
                { key: "adjustment", label: "Potongan / Tambahan Harga", sortable: true },
                { key: "subtotal", label: "Subtotal", sortable: true },
                { key: "action", label: "Aksi" },
            ],

            activeItem: {
                id: null,
                no_faktur_pgi: "",
                photo: ""
            },

            edit: {
                customer: "",
                optCustomer: []
            },

            sortBy: null,
            sortDesc: false,

            invoiceSize: 'A5',
            invoiceSizeCooldown: false

        }
    },

    watch: {

        "finalize.dp": function(newVal) {

            let cleaned = String(newVal).replace(/\./g, "")
            let res = cleaned.replace(/(?!^-)[^0-9]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
            this.$nextTick(() => this.finalize.dp = res)

        }

    },

    methods: {

        init() {

            this.initStatus = 0

            let getData = this.getItems()

            let getCustomers = this.$axios.get("customer/selection").then(res => {
                this.edit.optCustomer = res.data.data
            }).catch(error => this.$helper.parseError(this, error))

            Promise.all([getData, getCustomers]).then(() => {
                return Promise.all([
                    this.$axios.get("pphs/options").then(res => {
                        this.optPph = res.data.data
                    }),
                    this.$axios.get("stamp-duties/options").then(res => {
                        this.optMaterai = res.data.data
                    })
                ])
            }).then(() => {
                this.initStatus = 1
            }).catch(() => this.initStatus = -1)

        },

        onSortChanged(ctx) {
            this.sortBy = ctx.sortBy
            this.sortDesc = ctx.sortDesc
            this.getItems()
        },

        getItems() {
            let param = ""

            if(!this.$helper.isBlank(this.sortBy)) {
                param += "?sortBy=" + this.sortBy
                param += "&sortDesc=" + this.sortDesc
            }

            return this.$axios.get("invoice/draft-gold/" + this.$route.params.id + param).then(res => {
                this.data = res.data.data
            }).catch(error => this.$helper.parseError(this, error))

        },

        findItem() {

            if(this.$helper.isBlank(this.input.noFaktur)) {
                return this.$helper.toastError(this, "Mohon mengisi nomor faktur!")
            }

            this.invoiceDetailId = null
            this.noFakturPGI = this.input.noFaktur
            this.$bvModal.show("m-item-to-add")

        },

        promptRemoveItem(item) {

            let confirmed = confirm(`Hapus ${item.type} (NF ${item.no_faktur_pgi}) dari invoice ini?`)
            if(!confirmed) return

            this.activeItem = item
            this.afterPin = "cancel-item"
            this.pinTitle = "Pembatalan Barang - NF " + item.no_faktur_pgi
            this.$bvModal.show("m-pin")

        },

        execRemoveItem() {

            let payload = {
                invoice_detail_id: this.activeItem.id,
                invoice_id: this.$route.params.id,
                pin: this.pin
            }

            this.$axios.post("invoice/remove-item", payload).then(() => {
                this.$helper.toastSuccess(this, "Barang berhasil dihapus dari invoice!")
                this.pin = ""
                this.init()
            })
            .catch(error => this.$helper.parseError(this, error))

        },

        onItemModalHide() {
            this.noFakturPGI = ""
            this.invoiceDetailId = null
            this.getItems()
        },

        onItemAdded(toastMessage) {

            this.$bvModal.hide("m-item-to-add")
            this.$helper.toastSuccess(this, toastMessage)
            this.input.noFaktur = ""
            this.noFakturPGI = ""
            this.invoiceDetailId = null
            this.init()

        },

        promptCancelInvoice() {

            let isConfirmed = confirm("Batalkan invoice ini? Invoice akan dihapus dan barang-barang akan dikembalikan ke status semula!")
            if(!isConfirmed) return

            this.afterPin = "cancel-invoice"
            this.pinTitle = "Pembatalan Invoice"
            this.$bvModal.show("m-pin")

        },

        promptEditInvoice() {

            this.$bvModal.show("m-edit")

        },

        confirmEditInvoice() {

            this.afterPin = "edit-customer"
            this.pinTitle = "Perubahan Customer"
            this.$bvModal.show("m-pin")

        },

        execEditInvoice() {

            this.isSubmitting = true
            let payload = {
                id: this.$route.params.id,
                customer_id: this.edit.customer.id
            }

            this.$axios.post("invoice/update-invoice-draft", payload).then(() => {
                this.$helper.toastSuccess(this, "Customer Invoice berhasil diubah!")
                this.pin = ""
                this.init()
                this.$bvModal.hide("m-edit")
            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false)

        },

        execCancelInvoice() {

            let payload = {
                id: this.$route.params.id,
                pin: this.pin
            }

            this.$axios.post("invoice/cancel", payload).then(() => {
                this.$router.push({ path: "/sales/invoice" })
            })
            .catch(error => this.$helper.parseError(this, error))

        },

        finalizeInvoice() {
            if(!this.data.items.length) {
                return this.$helper.toastError(this, "Invoice masih kosong / tidak ada barang!")
            }

            this.$bvModal.show("m-finalize-disc-gold")
        },

        moveToConfirmation() {

            if (this.$helper.isBlank(this.finalize.noRef)) {
                return this.$helper.toastError(this, "No.Ref wajib diisi");
            }

            if (this.$helper.isBlank(this.finalize.soldAt)) {
                return this.$helper.toastError(this, "Tanggal penjualan wajib diisi");
            }

            if (this.$helper.isBlank(this.finalize.finalPrice) && this.isMeltedSale) {
                return this.$helper.toastError(this, "Harga final wajib diisi");
            }

            const payload = {
                id: this.$route.params.id,
                stage: 2,
                no_ref: this.finalize.noRef,
                sold_at: this.finalize.soldAt,
                final_price: this.finalize.finalPrice ? this.$helper.toNumber(this.finalize.finalPrice) : null,
                invoice_type: this.data.invoice_type
            }
            payload.note = this.finalize.note
            payload.pph_id = this.finalize.pph ? this.finalize.pph.id : null
            payload.stamp_duty_id = this.finalize.materai ? this.finalize.materai.id : null
            if(typeof payload === "string") return this.$helper.toastError(this, payload)


            this.isSubmitting = true
            this.$axios.post("invoice/finalize-gold", payload).then(res => {

                this.finalize.totalPriceAfterDiscount = res.data.data.total_price_after_discount
                this.finalData = res.data.data
                this.$bvModal.show("m-finalize-confirm")

            })
                .catch(error => {

                    let errMsg = "";
                    if (typeof error.response.data.error === "string") {
                        errMsg = error.response.data.error;
                    }

                    this.$helper.parseError(this, error)

                    // for check when customer has reached the limit of puitang
                    if (errMsg.includes("Piutang melebihi limit")) {
                        this.$bvModal.show("m-overlimit-request");
                    }
                })
            .finally(() => this.isSubmitting = false)

        },

        execConfirmInvoice() {

            let isConfirmed = confirm("Transaksikan invoice ini? Invoice yang sudah ditransaksikan tidak dapat dibatalkan!")
            if(!isConfirmed) return

            let payload = {
                id: this.$route.params.id,
                stage: 9,
                fullPayment: this.finalData.is_fully_paid,
                note: this.finalize.note,
                no_ref: this.finalData.no_ref,
                sold_at: this.finalData.sold_at,
                invoice_type: this.data.invoice_type,
                final_price: this.finalize.finalPrice ? this.$helper.toNumber(this.finalize.finalPrice) : null,
            }

            payload.pph_id = this.finalize.pph ? this.finalize.pph.id : null
            payload.stamp_duty_id = this.finalize.materai ? this.finalize.materai.id : null

            if(!this.$helper.isBlank(this.finalData.discount)){
                payload.discount = this.finalData.discount
            }
            if(!this.$helper.isBlank(this.finalData.discount_percent)){
                payload.discountPercent = this.finalData.discount_percent
            }

            if(this.finalData.is_fully_paid == false) {
                payload.dp = this.finalData.dp
            }

            this.isSubmitting = true
            this.$axios.post("invoice/finalize-gold", payload).then(res => {

                this.$bvModal.hide("m-finalize-confirm")
                this.invoiceLink = `${this.$BASE_PRINT_FILE_PATH}/${res.data.data.link}`
                this.invoiceLinkA4 = `${this.$BASE_PRINT_FILE_PATH}/${res.data.data.linka4}`
                this.$bvModal.show("m-print")

            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false)

        },

        finishInvoice() {
            this.$router.push({ path: "/sales/invoice" })
        },

        getInvoiceLink() {
            if(this.invoiceSize == 'A5'){
                return this.invoiceLink
            }
            else{
                return this.invoiceLinkA4
            }
        },

        toggleInvoiceSize() {
            this.invoiceSizeCooldown = true
            if(this.invoiceSize == 'A5') this.invoiceSize = 'A4'
            else this.invoiceSize = 'A5'
            setTimeout(function(){
                this.invoiceSizeCooldown = false
            }.bind(this),2000);
        },

        getFinalizePayload(stage) {

            let payload = {
                id: this.$route.params.id,
                stage: stage
            }

            if(stage >= 2) { // Diskon
                payload.no_ref = this.finalize.noRef
                payload.sold_at = this.finalize.soldAt
            }

            if(stage >= 3) { // DP dan pembayaran

                payload.fullPayment = this.finalize.fullPayment
                if(!this.finalize.fullPayment) {

                    if(this.$helper.isBlank(this.finalize.dp)) {
                        return "Mohon mengisi jumlah DP yang akan dibayarkan!"
                    }

                    let dp = this.$helper.toNumber(this.finalize.dp)
                    if(dp <= 0) return "DP tidak boleh lebih kecil dari nol!"
                    if(dp > this.finalize.totalPriceAfterDiscount) return "DP lebih besar daripada total harga! Gunakan metoda pembayaran lunas!"

                    payload.dp = dp

                }

            }

            return payload

        },

        promptEditItem(item) {

            this.activeItem = item
            this.afterPin = "edit-item"
            this.pinTitle = "Edit Barang - NF " + item.no_faktur_pgi
            this.$bvModal.show("m-pin")

        },

        execEditItem() {
            this.invoiceDetailId = this.activeItem.id
            this.noFakturPGI = this.activeItem.no_faktur_pgi
            this.$bvModal.show("m-item-to-add")
        },

        switchSegment(newSegment) {

            if(newSegment == this.data.price_segment) {
                return
            }

            let isConfirmed = confirm("Ubah segmen harga untuk invoice ini?")
            if(!isConfirmed) return

            this.isSubmitting = true
            let payload = {
                invoice_id: this.$route.params.id,
                price_segment: newSegment
            }

            this.$axios.post("invoice/switch-segment", payload).then(() => {

                this.$bvModal.hide("m-pick-price-segment")
                this.$helper.toastSuccess(this, "Segmen harga berhasil diubah!")
                this.init()

            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false)

        },

        checkPIN() {

            if(this.pin == "") return this.$helper.toastError(this, "PIN kosong!")

            let payload = {
                pin: this.pin
            }

            this.isSubmitting = true
            this.$axios.post("invoice/check-pin", payload).then(() => {

                this.$bvModal.hide("m-pin")

                switch(this.afterPin) {
                    case "cancel-invoice":
                        this.execCancelInvoice()
                        break

                    case "cancel-item":
                        this.execRemoveItem()
                        break

                    case "edit-customer":
                        this.execEditInvoice()
                        break

                    case "edit-item":
                        this.execEditItem()
                        this.pin = ""
                        break

                    default: break

                }

            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false)

        },

        parsePriceSegment(segment) {
            switch(segment) {
                case 0: return "Draft"
                case 1: return "Grosir Offline"
                case 2: return "Grosir Online"
                case 3: return "Retail"
                default: return "-"
            }
        },

        sanitizeDiscount() {
            let cleaned = String(this.finalize.discount).replace(/\./g, "")
            let res = cleaned.replace(/(?!^-)[^0-9]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
            this.finalize.discount = res
        },

        sanitizeDiscountAll() {
            let cleaned = String(this.finalize.discountAll).replace(/\./g, "")
            let res = cleaned.replace(/(?!^-)[^0-9]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
            this.finalize.discountAll = res
        },
        requestOverlimit() {
            let payload = {
                customer_id: this.data.customer_id,
                invoice_id: this.$route.params.id
            }

            this.isSubmitting = true;
            this.$axios.post("overlimit-request", payload).then(() => {
                this.$helper.toastSuccess(this, "Permintaan overlimit berhasil dikirim!")
            })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => {
                    this.isSubmitting = false
                    this.$bvModal.hide("m-overlimit-request")
                    this.$bvModal.hide("m-finalize-disc-gold")
                    this.getItems();
                })
        },
        openModalDiscountPurity() {
            this.finalize.discountPurity = "";
            this.$bvModal.show("m-discount-purity")
        },
        submitDiscountPurity() {

            if (this.finalize.discountPurity < -1000 || this.finalize.discountPurity > 1000) {
                return this.$helper.toastError(this, "Diskon kadar emas harus antara -1000 sampai 1000")
            }

            this.isSubmitting = true

            const payload = {
                invoice_id: this.$route.params.id,
                discount_purity: Number(this.finalize.discountPurity)
            }
            this.$axios.post("invoice/calculate-discount-purity", payload).then(() => {
                this.$bvModal.hide("m-discount-purity")
                this.init();
            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false);
        },
        openModalImport() {
            this.$bvModal.show('m-upload');
            this.input.fileUpload = null;
        },
        importExcel() {
            this.isSubmitting = true;
            if (!this.input.fileUpload) {
                this.$helper.toastError(this, "File tidak ditemukan!");
                this.isSubmitting = false;
                return;
            }
            const formData = new FormData();
            formData.append("file", this.input.fileUpload);
            formData.append("invoice_id", Number(this.$route.params.id));
            formData.append("invoice_type", this.data.invoice_type)
            this.$axios.post("invoice/item-gold/import", formData, {
                headers: {
                    "Content-Type": "multipart/form-data"
                }
            }).then(() => {
                this.$helper.toastSuccess(this, "Berhasil mengupload file!");
                this.input.fileUpload = null;
                this.$bvModal.hide("m-upload");
                this.init();
            }).catch((error) => {
                this.$helper.parseError(this, error);
            }).finally(() => {
                this.isSubmitting = false;
            });
        },
        sanitizeCapital() {
            let cleaned = String(this.finalize.finalPrice).replace(/\./g, "")
            let res = cleaned.replace(/(?!^-)[^0-9]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
            this.finalize.finalPrice = res
        },
        getAdjustmentValue(item) {
            if (item.discount_value) {
                return -Math.abs(item.discount_value)
            }
            return item.adjustment_value || 0
        }
    },
    created() {

        this.init()

        let tzoffset = (new Date()).getTimezoneOffset() * 60000
        let localISOTime = (new Date(Date.now() - tzoffset)).toISOString().slice(0, -1)
        this.finalize.soldAt = localISOTime.substring(0, 10)

    },
    computed: {
        statusOverlimitBadge() {
            switch(this.data.over_limit_request_status) {
                case 0: return "warning"
                case 1: return "success"
                case 2: return "danger"
                default: return "warning"
            }
        },
        statusOverlimit() {
            switch(this.data.over_limit_request_status) {
                case 0: return "Pending"
                case 1: return "Diterima"
                case 2: return "Ditolak"
                default: return "Pending"
            }
        },
        isGoldDivision() {
            return this.data.division == "EMAS";
        },
        invoiceTypeLabel() {
            const invoiceType = this.data.invoice_type;
            switch (invoiceType) {
                case 'AUCTION':
                    return 'Lelang'
                case 'ORIGINAL_PRICE':
                    return 'Harga Asli'
                case 'MELTED_SALE':
                    return 'Jual Lebur'
                case 'BUYBACK':
                    return 'Buyback'
                default:
                    return '';
            }
        },
        isAuction() {
            return this.data.invoice_type === "AUCTION";
        },
        isOriginalPrice() {
            return this.data.invoice_type === "ORIGINAL_PRICE";
        },
        isMeltedSale() {
            return this.data.invoice_type === "MELTED_SALE";
        },
        isBuyback() {
            return this.data.invoice_type === "BUYBACK";
        },
        filteredFields() {
            if (this.data.invoice_type === "MELTED_SALE") {
                const allowedKeys = ["barang", "noFakturImei", "action"];
                return this.fields.filter(field => allowedKeys.includes(field.key));
            }
            return this.fields;
        }
    }
}
</script>

<style>

</style>
